(() => {
  'use strict';

  class Iphone18BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.selection = new Map();
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
    }

    connectedCallback() {
      // 按件数从高到低排序，后台即使把两档件数填反也能取到正确档位；折扣允许为 0，不能用 || 兜底
      this.tiers = [
        { quantity: this.readNumber(this.dataset.tierOneQuantity, 2), discount: this.readNumber(this.dataset.tierOneDiscount, 15) },
        { quantity: this.readNumber(this.dataset.tierTwoQuantity, 3), discount: this.readNumber(this.dataset.tierTwoDiscount, 20) },
      ].sort((a, b) => b.quantity - a.quantity);
      this.summary = this.querySelector('[data-summary]');
      this.selectionList = this.querySelector('[data-selection-list]');
      this.selectionTemplate = this.querySelector('[data-selection-template]');
      this.cartButton = this.querySelector('[data-add-selection]');
      this.tracks = Array.from(this.querySelectorAll('[data-product-track]'));

      this.querySelectorAll('[data-bundle-product]').forEach((card) => this.prepareCard(card));
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.tracks.forEach((track) => {
        track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.updateTrack(track);
      });
      this.renderSummary();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.tracks.forEach((track) => track.removeEventListener('scroll', this.handleScroll));
    }

    prepareCard(card) {
      const variantData = card.querySelector('[data-product-variants]');
      try {
        card.variants = JSON.parse(variantData?.textContent || '[]');
      } catch (error) {
        card.variants = [];
      }
    }

    handleClick(event) {
      const previous = event.target.closest('[data-scroll-previous]');
      const next = event.target.closest('[data-scroll-next]');
      const selectButton = event.target.closest('[data-select-product]');
      const summaryToggle = event.target.closest('[data-summary-toggle]');
      const selectionItem = event.target.closest('[data-selection-item]');

      if (previous || next) {
        const group = event.target.closest('[data-bundle-group]');
        const track = group?.querySelector('[data-product-track]');
        if (track) {
          const card = track.querySelector('[data-bundle-product]');
          const distance = (card?.getBoundingClientRect().width || 300) + 16;
          track.scrollBy({ left: previous ? -distance : distance, behavior: 'smooth' });
        }
        return;
      }

      if (selectButton) {
        this.toggleProduct(selectButton.closest('[data-bundle-product]'));
        return;
      }

      if (summaryToggle && window.matchMedia('(max-width: 768px)').matches) {
        const expanded = this.summary.classList.toggle('is-expanded');
        summaryToggle.setAttribute('aria-expanded', String(expanded));
        return;
      }

      if (selectionItem) {
        const key = selectionItem.dataset.selectionKey;
        if (event.target.closest('[data-quantity-increase]')) this.changeQuantity(key, 1);
        if (event.target.closest('[data-quantity-decrease]')) this.changeQuantity(key, -1);
        if (event.target.closest('[data-selection-remove]')) this.removeSelection(key);
        return;
      }

      if (event.target.closest('[data-add-selection]')) this.addSelectionToCart();
    }

    handleChange(event) {
      if (!event.target.matches('[data-option-input]')) return;
      const card = event.target.closest('[data-bundle-product]');
      if (card) this.updateVariant(card, event.target);
    }

    handleScroll(event) {
      this.updateTrack(event.currentTarget);
    }

    updateVariant(card, changedInput) {
      const values = Array.from(card.querySelectorAll('[data-option-index]')).map((option) => {
        const checked = option.querySelector('input[data-option-input]:checked');
        const select = option.querySelector('select[data-option-input]');
        return checked?.value || select?.value || '';
      });
      const changedOption = changedInput.closest('[data-option-index]');
      const changedIndex = Number(changedOption?.dataset.optionIndex);
      const variant = card.variants.find((item) => (
        item.options.every((value, index) => String(value).trim() === String(values[index]).trim())
      )) || card.variants.find((item) => (
        Number.isInteger(changedIndex)
        && String(item.options[changedIndex]).trim() === String(changedInput.value).trim()
      ));
      if (!variant) return;

      this.syncOptionInputs(card, variant);

      card.dataset.variantId = variant.id;
      card.dataset.price = variant.price;
      const price = card.querySelector('[data-product-price]');
      const button = card.querySelector('[data-select-product]');
      const image = card.querySelector('.iphone-18-bundle-builder__image');
      const selectedColor = card.querySelector('input[type="radio"][data-option-input]:checked');
      const colorName = selectedColor?.closest('[data-option-index]')?.querySelector('[data-color-name]');

      if (price) price.textContent = `${this.formatMoney(variant.price)}（税込）`;
      if (button) button.disabled = !variant.available;
      if (colorName && selectedColor) colorName.textContent = ` - ${selectedColor.value}`;
      if (image && variant.featured_image?.src) {
        image.src = variant.featured_image.src;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      }

      const key = String(card.dataset.productId);
      const selected = this.selection.get(key);
      if (selected) {
        if (!variant.available) {
          this.removeSelection(key);
          return;
        }
        this.selection.set(key, this.getCardSelection(card, selected.quantity));
        this.renderSummary();
      }
    }

    syncOptionInputs(card, variant) {
      card.querySelectorAll('[data-option-index]').forEach((option, index) => {
        const value = String(variant.options[index] ?? '').trim();
        const select = option.querySelector('select[data-option-input]');
        const radios = option.querySelectorAll('input[data-option-input]');
        if (select) select.value = value;
        radios.forEach((radio) => {
          radio.checked = String(radio.value).trim() === value;
        });
      });
    }

    toggleProduct(card) {
      if (!card) return;
      const key = String(card.dataset.productId);
      if (this.selection.has(key)) {
        this.removeSelection(key);
        return;
      }

      this.selection.set(key, this.getCardSelection(card, 1));
      this.setCardSelected(card, true);
      this.renderSummary();
    }

    getCardSelection(card, quantity) {
      return {
        card,
        variantId: Number(card.dataset.variantId),
        title: card.querySelector('.iphone-18-bundle-builder__product-title')?.textContent.trim() || '',
        price: Number(card.dataset.price) || 0,
        image: card.querySelector('.iphone-18-bundle-builder__image')?.currentSrc || card.querySelector('.iphone-18-bundle-builder__image')?.src || '',
        quantity,
      };
    }

    setCardSelected(card, selected) {
      const button = card?.querySelector('[data-select-product]');
      if (!button) return;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
      const addLabel = button.querySelector('[data-add-label]');
      const removeLabel = button.querySelector('[data-remove-label]');
      if (addLabel) addLabel.hidden = selected;
      if (removeLabel) removeLabel.hidden = !selected;
    }

    changeQuantity(key, change) {
      const selected = this.selection.get(key);
      if (!selected) return;
      const quantity = selected.quantity + change;
      if (quantity < 1) {
        this.removeSelection(key);
        return;
      }
      selected.quantity = quantity;
      this.renderSummary();
    }

    removeSelection(key) {
      const selected = this.selection.get(key);
      if (!selected) return;
      this.setCardSelected(selected.card, false);
      this.selection.delete(key);
      this.renderSummary();
    }

    renderSummary() {
      if (!this.selectionList || !this.selectionTemplate) return;
      this.selectionList.replaceChildren();
      this.selection.forEach((selected, key) => {
        const item = this.selectionTemplate.content.firstElementChild.cloneNode(true);
        item.dataset.selectionKey = key;
        const image = item.querySelector('[data-selection-image]');
        if (image && selected.image) image.src = selected.image;
        item.querySelector('[data-selection-title]').textContent = selected.title;
        item.querySelector('[data-selection-price]').textContent = this.formatMoney(selected.price);
        item.querySelector('[data-selection-quantity]').textContent = selected.quantity;
        this.selectionList.append(item);
      });

      const count = Array.from(this.selection.values()).reduce((total, item) => total + item.quantity, 0);
      // subtotal = 折前原价，total = 应用数量阶梯折扣后的价格；两者都是最小货币单位的整数，避免浮点误差
      const subtotal = Array.from(this.selection.values()).reduce((sum, item) => sum + item.price * item.quantity, 0);
      const discount = this.tiers.find((tier) => count >= tier.quantity)?.discount || 0;
      const total = discount ? Math.round((subtotal * (100 - discount)) / 100) : subtotal;
      const progress = Math.min(100, (count / this.tiers[0].quantity) * 100);

      this.querySelectorAll('[data-item-count]').forEach((element) => { element.textContent = count; });
      this.querySelectorAll('[data-total], [data-mobile-total]').forEach((element) => { element.textContent = this.formatMoney(total); });
      const discountValue = this.querySelector('[data-discount-value]');
      const progressBar = this.querySelector('[data-progress]');
      if (discountValue) discountValue.textContent = discount ? `${discount}%OFF` : '0%';
      if (progressBar) progressBar.style.width = `${progress}%`;
      if (this.cartButton) this.cartButton.disabled = count === 0;
    }

    updateTrack(track) {
      const group = track.closest('[data-bundle-group]');
      const previous = group?.querySelector('[data-scroll-previous]');
      const next = group?.querySelector('[data-scroll-next]');
      const thumb = group?.querySelector('[data-scroll-thumb]');
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      if (previous) previous.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
      if (!thumb) return;
      const thumbWidth = Math.max(25, (track.clientWidth / track.scrollWidth) * 100);
      const position = maxScroll ? (track.scrollLeft / maxScroll) * (100 - thumbWidth) : 0;
      thumb.style.width = `${Math.min(100, thumbWidth)}%`;
      thumb.style.left = `${position}%`;
    }

    readNumber(value, fallback) {
      const number = Number(value);
      return value === undefined || value === '' || Number.isNaN(number) ? fallback : number;
    }

    formatMoney(cents) {
      const locale = document.documentElement.lang || 'ja-JP';
      const currency = window.Shopify?.currency?.active || 'JPY';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'JPY' ? 0 : 2,
      }).format(cents / 100);
    }

    getServiceSections() {
      const sectionIds = [];
      const cartMini = document.querySelector('xo-cart-mini');
      const cart = document.querySelector('xo-cart');
      if (cartMini?.id) sectionIds.push(cartMini.id);
      if (window.location.pathname.includes('/cart') && cart?.id) sectionIds.push(cart.id);
      return sectionIds.length ? sectionIds : ['cart-drawer', 'cart-icon-bubble'];
    }

    async addSelectionToCart() {
      if (!this.cartButton || this.cartButton.disabled || !this.selection.size) return;
      const items = Array.from(this.selection.values()).map((item) => ({
        id: item.variantId,
        quantity: item.quantity,
      }));
      const quantity = items.reduce((total, item) => total + item.quantity, 0);
      const root = window.Shopify?.routes?.root || '/';
      this.cartButton.disabled = true;

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: this.getServiceSections(),
            section_url: window.location.pathname,
          }),
        });
        if (!response.ok) throw new Error('add-to-cart-failed');
        const data = await response.json();
        this.syncCart(data, items, quantity);
        const trigger = document.querySelector('xo-modal-trigger[xo-name="cart"]');
        if (trigger) trigger.click();
        else window.location.href = `${root}cart`;
      } catch (error) {
        this.cartButton.disabled = false;
        this.notify(this.dataset.cartError || 'カートに追加できませんでした');
      }
    }

    syncCart(data, items, quantity) {
      if (window.xoStore && typeof window.xoStore.set === 'function') {
        try {
          window.xoStore.set('xo-cart', (previous) => ({
            ...previous,
            size: ((previous && previous.size) || 0) + quantity,
            sections: data.sections,
            items: data.items,
            addIdLoading: '',
            isAdded: true,
            productIdsForCartNotification: items.map((item) => String(item.id)),
          }))('xo-cart/addCartSuccess');
        } catch (error) {
          // The cart page redirect remains a fallback if the theme store contract changes.
        }
      }
      this.cartButton.disabled = false;
    }

    notify(message) {
      if (window.xoToast && typeof window.xoToast.push === 'function') {
        window.xoToast.push({ content: message, className: 'xo-cart-add-error', placement: 'top-center', delay: 4000 });
      } else {
        window.alert(message);
      }
    }
  }

  if (!customElements.get('iphone-18-bundle-builder')) {
    customElements.define('iphone-18-bundle-builder', Iphone18BundleBuilder);
  }
})();